'use client';

import { useEffect } from 'react';

import { RainbowKitProvider, useConnectModal } from '@rainbow-me/rainbowkit';
import { disconnect } from '@wagmi/core';
import { useAccount } from 'wagmi';

import { chains } from '@web/utils/rainbowkit/chains';
import { wagmiConfig } from '@web/utils/rainbowkit/WagmiProvider';

interface IProps {
  onSettled: () => void;
}

// Mounted only while we're re-pairing wagmi with the underlying wallet
// (JWT is still valid; we just need eth_accounts to come back non-empty).
// No auth adapter → RainbowKit's modal acts as a pure wallet picker; no SIWE
// re-prompt fires.
const _ReconnectInner = ({ onSettled }: IProps) => {
  const { openConnectModal, connectModalOpen } = useConnectModal();
  const wallet = useAccount();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Wagmi reports `status: 'connected'` from cached `wagmi.store` while
      // MetaMask has revoked site permission. Clearing first is what forces
      // a real `eth_requestAccounts` prompt on the next connect attempt.
      await disconnect(wagmiConfig);
      if (cancelled) return;
      openConnectModal?.();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!connectModalOpen && wallet.status !== 'reconnecting') {
      // Defer unmount slightly so RainbowKit's exit animation completes
      // before its provider disappears from the tree.
      const t = setTimeout(onSettled, 250);
      return () => clearTimeout(t);
    }
  }, [connectModalOpen, wallet.status, onSettled]);

  return null;
};

export const ReconnectWallet = ({ onSettled }: IProps) => (
  <RainbowKitProvider initialChain={chains.ethereum}>
    <_ReconnectInner onSettled={onSettled} />
  </RainbowKitProvider>
);
